import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Button, Grid, Stack } from '@mui/material';
import {
  DataGridPro,
  gridClasses,
  GridEventListener,
  type GridRowSelectionModel,
  type GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';

import { TrpcLoadingInfo } from '../../../components/general';
import { useVeranstaltungenStatusMutation } from '../../../components/general/hooks/useVeranstaltungenStatusMutation.ts';
import { PlanningWarningDialog } from '../../../components/general/PlanningWarningDialog.tsx';
import { useSnackbarFromGenerated } from '../../../components/snackbars/useSnackbarFromGenerated.ts';
import {
  generateVeranstaltungListingDeleteErrorSnackbar,
  generateVeranstaltungListingDeleteSuccessSnackbar,
} from '../../../components/snackbars/veranstaltungListingSnackbars';
import {
  type AkaVeranstaltungId,
  capabilities,
  type DeleteVeranstaltungenOutput,
  type ThemaFromThemaRouter,
  type VivaStatus,
  vivaStatus,
} from '../../../dtos';
import { trpc } from '../../../trpc';
import { localStorageApi, localStorageKeys } from '../../../utils';
import { getDetailPanelHeight } from '../../../utils/getDetailPanelHeight.ts';
import { isAkaVeranstaltungId } from '../../../utils/isAkaVeranstaltungId.ts';
import { sortVivaStatus } from '../../../utils/sortVivaStatus.ts';
import { unique } from '../../../utils/unique.ts';
import { ConfirmationDialog } from './ConfirmationDialog';
import { LeistungDetailPanel } from './LeistungDetailPanel.tsx';
import { content } from './VeranstaltungListing.content';
import { VeranstaltungElement } from './VeranstaltungListing.types.ts';
import { veranstaltungColumns } from './veranstaltungListingColumns.tsx';

type VeranstaltungListingProps = {
  readonly thema: ThemaFromThemaRouter;
};

export const VeranstaltungListing = ({ thema }: VeranstaltungListingProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueSnackbarFromGenerated } = useSnackbarFromGenerated();
  const veranstaltungenQuery = trpc.veranstaltung.getVeranstaltungenByAkaThemaIds.useQuery(thema.akaThemaId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusWarningDialogOpen, setStatusWarningDialogOpen] = useState(false);
  const [gridSelection, setGridSelection] = useState<GridRowSelectionModel>(() => ({
    type: 'include',
    ids: new Set(),
  }));
  const [filterModel, setFilterModel] = useState(
    localStorageApi.get(localStorageKeys.THEMA_VA_GRID_FILTERMODEL) ?? undefined,
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(
    localStorageApi.get(localStorageKeys.THEMA_VA_GRID_SORTMODEL) ?? [{ sort: 'desc', field: 'start' }],
  );

  const changeStatusMutation = useVeranstaltungenStatusMutation({ openLinksInNewTab: true });
  const apiRef = useGridApiRef();
  const onRowClick = useCallback<GridEventListener<'rowClick'>>(
    (params) => {
      apiRef.current?.toggleDetailPanel(params.id);
    },
    [apiRef],
  );

  const executeStatusChangeForSelection = async (newStatus: VivaStatus): Promise<void> => {
    const selectionArray = Array.from(gridSelection.ids);
    const akaVeranstaltungIds: AkaVeranstaltungId[] = selectionArray.map((selectedGrid) => {
      if (!isAkaVeranstaltungId(selectedGrid)) {
        throw new Error('Grid selection does not fit input model for changing status of VAs.');
      }

      return selectedGrid;
    });

    await changeStatusMutation.mutateAsync({
      newStatus,
      akaVeranstaltungIds,
    });
  };

  const handleChangeStatusForSelectedVAs = async (newStatus: VivaStatus): Promise<void> => {
    const selectionArray = Array.from(gridSelection.ids);
    if (selectionArray.length === 0) {
      enqueueSnackbar(content.keineVeranstaltungenAuswahl, { variant: 'warning' });
      return;
    }

    if (
      newStatus === vivaStatus.FREIGEGEBEN &&
      selectedVeranstaltungen.some(
        (veranstaltung) => veranstaltung.manuellerAusschreibungsprozess || veranstaltung.isKurzfristigerErsatztermin,
      )
    ) {
      setStatusWarningDialogOpen(true);
      return;
    }

    await executeStatusChangeForSelection(newStatus);
  };

  const handleAbortFreigeben = () => {
    setStatusWarningDialogOpen(false);
  };

  const handleConfirmFreigeben = async () => {
    setStatusWarningDialogOpen(false);
    await executeStatusChangeForSelection(vivaStatus.FREIGEGEBEN);
  };

  const deleteVeranstaltungenMutation = trpc.veranstaltung.deleteVeranstaltungenByAkaVeranstaltungId.useMutation({
    onError: () => enqueueSnackbar(content.deleteVeranstaltungenError, { variant: 'error', persist: true }),
    onSuccess: (mutateResult: DeleteVeranstaltungenOutput) => {
      if (mutateResult.successfulDeletions.length === 0 && mutateResult.failedDeletions.length === 0) {
        enqueueSnackbar(content.keineVeranstaltungenAenderung, { variant: 'info' });
        return;
      }

      if (mutateResult.successfulDeletions.length > 0) {
        enqueueSnackbarFromGenerated(
          generateVeranstaltungListingDeleteSuccessSnackbar({
            successfulDeletions: mutateResult.successfulDeletions,
          }),
        );
      }

      if (mutateResult.failedDeletions.length > 0) {
        enqueueSnackbarFromGenerated(
          generateVeranstaltungListingDeleteErrorSnackbar({
            failedDeletions: mutateResult.failedDeletions,
          }),
        );
      }
    },
  });

  const handleDeleteSelectedVAs = async (): Promise<void> => {
    const selectionArray = Array.from(gridSelection.ids);
    if (selectionArray.length === 0) {
      enqueueSnackbar(content.keineVeranstaltungenAuswahl, { variant: 'warning' });
      return;
    }

    const akaVeranstaltungIds: AkaVeranstaltungId[] = selectionArray.map((id) => {
      if (!isAkaVeranstaltungId(id)) {
        throw new Error('Grid selection does not fit input model for deleting VAs.');
      }

      return id;
    });

    await deleteVeranstaltungenMutation.mutateAsync({ akaVeranstaltungIds });
    setDialogOpen(false);
  };

  const selectedVeranstaltungen = (veranstaltungenQuery.data ?? []) //
    .filter((veranstaltung) => gridSelection.ids.has(veranstaltung.akaVeranstaltungId));

  const statusTransitionsToShow = selectedVeranstaltungen
    .flatMap((veranstaltung) => veranstaltung.statusTransitions)
    .filter(unique)
    .toSorted(sortVivaStatus);

  const isDeleteActionAvailable = selectedVeranstaltungen
    .flatMap((va) => va.capabilities)
    .includes(capabilities.DELETE);

  return (
    <>
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <Grid container justifyContent="end" sx={{ paddingBottom: 1 }}>
            {gridSelection.ids.size > 0 && (
              <Grid size={{ xs: 12 }} gap={2} display="flex">
                {isDeleteActionAvailable && (
                  <Button
                    disabled={changeStatusMutation.isPending}
                    variant="outlined"
                    color="error"
                    onClick={() => setDialogOpen(true)}
                  >
                    {content.buttons.loeschen}
                  </Button>
                )}

                {statusTransitionsToShow.map((newStatus) => (
                  <Button
                    key={newStatus}
                    disabled={changeStatusMutation.isPending}
                    variant="outlined"
                    onClick={async () => await handleChangeStatusForSelectedVAs(newStatus)}
                  >
                    {content.buttons[newStatus]}
                  </Button>
                ))}
              </Grid>
            )}
          </Grid>
          <Stack spacing={1}>
            <TrpcLoadingInfo trpcQuery={veranstaltungenQuery} entity="Veranstaltungen">
              {veranstaltungenQuery.data && (
                <DataGridPro<VeranstaltungElement>
                  apiRef={apiRef}
                  disableRowSelectionExcludeModel
                  columns={veranstaltungColumns()}
                  rows={veranstaltungenQuery.data.map((veranstaltung) => ({
                    ...veranstaltung,
                    thema,
                  }))}
                  checkboxSelection
                  isRowSelectable={(params) =>
                    params.row.capabilities.includes(capabilities.DELETE) || params.row.statusTransitions.length > 0
                  }
                  pagination
                  pageSizeOptions={[5, 10, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  filterModel={filterModel}
                  onFilterModelChange={(newFilterModel) => {
                    setFilterModel(newFilterModel);
                    localStorageApi.set(localStorageKeys.THEMA_VA_GRID_FILTERMODEL, newFilterModel);
                  }}
                  sortModel={sortModel}
                  onSortModelChange={(newSortModel) => {
                    setSortModel(newSortModel);
                    localStorageApi.set(localStorageKeys.THEMA_VA_GRID_SORTMODEL, newSortModel);
                  }}
                  onRowSelectionModelChange={(newSelection) => {
                    setGridSelection(newSelection);
                  }}
                  rowSelectionModel={gridSelection}
                  disableRowSelectionOnClick
                  sx={{
                    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                      outline: 'none',
                    },
                    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                      outline: 'none',
                    },
                  }}
                  getRowId={(veranstaltung) => veranstaltung.akaVeranstaltungId}
                  getDetailPanelContent={LeistungDetailPanel}
                  getDetailPanelHeight={getDetailPanelHeight}
                  slots={{
                    detailPanelExpandIcon: () => <ExpandMore sx={{ p: 0 }} />,
                    detailPanelCollapseIcon: () => <ExpandLess sx={{ p: 0 }} />,
                  }}
                  onRowClick={onRowClick}
                />
              )}
            </TrpcLoadingInfo>
          </Stack>
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={dialogOpen}
        title={content.deleteDialog.title}
        content={content.deleteDialog.content}
        onApprove={async () => {
          await handleDeleteSelectedVAs();
          setDialogOpen(false);
        }}
        onClose={() => setDialogOpen(false)}
      />

      <PlanningWarningDialog
        open={statusWarningDialogOpen}
        warnings={[
          {
            id: 'manuellerAusschreibungsprozess',
            title: content.freigabeWarningDialog.title,
            message: selectedVeranstaltungen.some((veranstaltung) => veranstaltung.manuellerAusschreibungsprozess)
              ? content.freigabeWarningDialog.contentVAManuellerAusschreibungsprozess
              : content.freigabeWarningDialog.contentVAKurzfristigerErsatztermin,
          },
        ]}
        onCancel={handleAbortFreigeben}
        onApprove={handleConfirmFreigeben}
        loading={changeStatusMutation.isPending}
        approveButtonLabel={content.freigabeWarningDialog.approveButtonLabel}
        questionText={content.freigabeWarningDialog.questionText}
      />
    </>
  );
};
